'use strict';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import urljoin from 'url-join';
import { Redirect, Route, Switch } from 'react-router-dom';
import classNames from 'classnames';
import { usePendo } from '@/hooks/usePendo';
import { useBiovizStatus } from '@/lenses/uilenses/hooks/biovizStatus/biovizStatus.hook';
import * as enums from '@/enums/Enums';
import Sort from '../../utils/Sort.js';
import SettingsMap from '../../utils/SettingsMap.js';
import { convertDate } from '../../utils/DateUtils';

import { LoadingSpinner } from 'metabux/components/LoadingSpinner/LoadingSpinner';
import { Navigation } from '@/lenses/uilenses/components/Navigation/Navigation';
import { Link, Toast } from '../../components/index.jsx';

import ProjectFiles from './files/ProjectFiles.jsx';
import PathwayExplorer from './pathways/PathwayExplorer.jsx';
import Heatmap from './Heatmap/Heatmap.jsx';
import ProjectHome from './Home/ProjectHome';

import '../../theme/theme.css';
import '../../theme/css/material-icons.css';
import './ProjectContainer.scss';
import { SpectralData } from '../spectralData/SpectralData';
import { useFetchSpectralMetaDataQuery } from '@/redux/services/portal/spectralData.api';
import { usePermissions } from '../../hooks/permissions.hook.jsx';
import * as permissions from '../../security/permissionTypes';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEmptyQuery } from '@/redux/services/portal/projects.api';
import { PipelineStatusPanel } from '@/features/pipelineStatus/PipelineStatusPanel';
import { useCanAccessProjectPipelineStatus, usePipelineStatus, useResolvedPipelineStatusFlowScenario } from '@/features/pipelineStatus/usePipelineStatus';
import { usePipelineStatusHub } from '@/features/pipelineStatus/usePipelineStatusHub';
import { useDiscoveryPanelEntries } from '@/features/discoveryPanels/useDiscoveryPanelEntries';
import { buildProjectTools } from '@/features/projectTools/projectToolsBuilder';
import { projectToolsToSidebarItems } from '@/features/projectTools/projectToolsAdapters';
import { API_BIOVIZ, ENABLE_PIPELINE_STATUS } from '@/config/env.config';

export const Project = ({
	className,
	match,
	loading,
	adminProjectData,
	displayProjectId,
	displayTitle,
	project,
	projectCode,
	projectId,
	isLoading,
	showToaster,
	sampleSets,
	sampleSetIndex,
	statsId,
	plants,
	animals,
}) => {
	const pendo = usePendo();
	const { status: biovizStatus, href, isLoading: biovizLoading } = useBiovizStatus(projectId);
	const { data: spectralData } = useFetchSpectralMetaDataQuery(projectId, { skip: !projectId});
	const hasSpectralData = useMemo(() => spectralData?.hasSpectralDataProject || false, [spectralData]);
	const { checkUserHasOneRole, checkUserHasRole, checkUserHasPermissions } = usePermissions();
	const emptyQueryArg = projectId ? projectId : skipToken;
	const {
		data: projectEmptyState,
		isLoading: emptyIsLoading,
		isFetching: emptyIsFetching,
	} = useEmptyQuery(emptyQueryArg);
	usePipelineStatusHub(projectId);
	const canAccessProjectPipelineStatus = useCanAccessProjectPipelineStatus();
	const pipelineStatusFlowScenario = useResolvedPipelineStatusFlowScenario(project?.pipeline);
	const pipelineStatus = usePipelineStatus(projectId, pipelineStatusFlowScenario);
	const projectHomeUrl = useMemo(() => urljoin(match.url, 'home'), [match.url]);
	const { entries: discoveryEntries, hasDiscoveryPanels } = useDiscoveryPanelEntries(projectId);

	const canonicalProjectTools = useMemo(
		() =>
			buildProjectTools({
				variant: 'inProject',
				hasDiscoveryPanels,
				discoveryEntries,
				projectCode: projectCode || null,
				projectBasePath: match.url,
				hasSpectralData,
				biovizStatus,
				biovizLoading,
				biovizHref: href,
				ibpFallbackHref: API_BIOVIZ,
			}),
		[
			hasDiscoveryPanels,
			discoveryEntries,
			projectCode,
			match.url,
			hasSpectralData,
			biovizStatus,
			biovizLoading,
			href,
		],
	);

	const items = useMemo(
		() => projectToolsToSidebarItems(canonicalProjectTools),
		[canonicalProjectTools],
	);

	useEffect(() => {
		pendo.identify(match.params.projectCode);
		return () => {
			pendo.identify();
		};
	}, [match.params.projectCode]);

	const visibleSampleSets = sampleSets?.concat().filter(sampleSet => sampleSet.customerVisible);
	//update VisibleStats
	const visibleSampleSetsUpdated = visibleSampleSets?.map(sampleSet => {
		const temp = { ...sampleSet };
		temp.stats = sampleSet.stats.filter(stat => stat.customerVisible).sort(Sort.numeric('customerIndex', 1));
		return temp;
	});

	if (emptyQueryArg === skipToken) return <LoadingSpinner />;
	if (emptyIsLoading || emptyIsFetching) return <LoadingSpinner />;
	if (projectEmptyState === undefined) return <LoadingSpinner />;

	if (projectEmptyState && checkUserHasOneRole(enums.ROLES.BASIC)) {
		return <Redirect to={projectHomeUrl} />;
	}

	return (
		<>
			<Navigation className="nav" items={items} sx={{ zIndex: 2 }} />
			<div className={classNames("ProjectContainer", className)}>
				<CodeDisplay projectId={project.projectId} projectCode={project.projectCode} displayProjectId={displayProjectId} showToaster={showToaster} />

				<div className="projectHeader">
					<div className="title" >{displayTitle}</div>
					{checkUserHasPermissions(permissions.CAN_EDIT_PROJECTS) && (
						<div className="publishDates">
							{project.lastPublishDate && (
								<span className="lastPublishDate">
									Last Published: {convertDate(project.lastPublishDate, 'MMM DD YYYY')}
								</span>
							)}
						</div>
					)}
				</div>
				<Switch>
					<Route path={match.url} exact>
						<Redirect to={projectHomeUrl} />
					</Route>
					<Route path={projectHomeUrl} exact>
						<>
							{canAccessProjectPipelineStatus && projectId && ENABLE_PIPELINE_STATUS && (
								<PipelineStatusPanel
									stages={pipelineStatus.stages}
									isLoading={pipelineStatus.isLoading}
									error={pipelineStatus.error}
								/>
							)}
							{sampleSets ? <ProjectHome sampleSets={sampleSets} match={match} projectId={projectId} /> : <LoadingSpinner />}
						</>
					</Route>
					<Route path={urljoin(match.url, "results")}>
						<ProjectFiles project={project} projectId={projectId} />
					</Route>
					<Route path={urljoin(match.url, "statstable")}>
						{visibleSampleSetsUpdated ?
							<Heatmap
								webWorker={true}
								project={project}
								sampleSets={visibleSampleSetsUpdated}
								sampleSetIndex={sampleSetIndex}
								statsId={statsId}
							/> :
							<LoadingSpinner />}
					</Route>
					<Route path={urljoin(match.url, "pathway-legacy")}>
						{((!visibleSampleSetsUpdated || loading || !(animals && plants)) ?
							<div className="PathwayExplorer"><LoadingSpinner /></div> :
							<PathwayExplorer
								webWorker={true}
								foldChangeScale={SettingsMap.layers.foldChangeScale}
								project={project}
								sampleSets={visibleSampleSetsUpdated}
								sampleSetIndex={sampleSetIndex}
								statsId={statsId}
								plants={plants}
								animals={animals}
							/>
						)}
					</Route>
					<Route path={urljoin(match.url, "pathwayexplorer")}>
						{((!visibleSampleSetsUpdated || loading || !(animals && plants)) ?
							<div className="PathwayExplorer"><LoadingSpinner /></div> :
							<PathwayExplorer
								webWorker={true}
								fireWorksLayout="d3"
								adminProjectData={adminProjectData}
								foldChangeScale={SettingsMap.layers.foldChangeScale}
								project={project}
								sampleSets={visibleSampleSetsUpdated}
								sampleSetIndex={sampleSetIndex}
								statsId={statsId}
								plants={plants}
								animals={animals
								} />
						)}
					</Route>

					<Route path={urljoin(match.url, "spectral-data")}>
						{projectId && <SpectralData match={match} projectCode={projectCode} projectId={projectId} />}
					</Route>
				</Switch>
				<Toast />
			</div>
		</>
	);
};

const CodeDisplay = ({ projectCode, projectId, displayProjectId, showToaster, ...props }) => {

	const [isHovering, setIsHovering] = useState(false);
	const { checkUserHasRole } = usePermissions();

	const onHover = useCallback(() => {
		setIsHovering(!isHovering);
	});

	const copyToClipboard = useCallback(() => {
		if (!checkUserHasRole(enums.ROLES.DTS)) { return; }
		let text = projectId;
		showToaster("Copying ProjectId ...", projectId);
		let dummy = document.createElement("textarea");
		document.body.appendChild(dummy);
		dummy.value = projectId;
		dummy.select();
		document.execCommand("copy");
		document.body.removeChild(dummy);
	}, [projectId]);

	return (
		<div onClick={copyToClipboard} className="projCode" onMouseEnter={onHover} onMouseLeave={onHover}>
			{projectCode} &nbsp; {isHovering ? displayProjectId : ''}

		</div>
	);
};